import { useCallback, useEffect, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import ReactPlayer from 'react-player/lazy';
import screenfull from 'screenfull';
import { detectSystemMobile } from 'utils';
import { ESystemMobile } from 'types';

type Props = {
  className?: string;
  url: string;
  isFullscreenOnclick?: boolean;
  isAutoPlayOnView?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  onLoad?: () => void;
  poster?: string;
  type: string;
};

function VideoPlayer({
  className,
  url,
  isFullscreenOnclick = false,
  isAutoPlayOnView = false,
  controls = true,
  autoPlay = true,
  loop = true,
  onLoad,
  poster,
  type,
}: Props) {
  const playerRef = useRef<any>(null);
  const videoRef = useRef<any>(null);

  const timeoutRef = useRef<NodeJS.Timeout>();
  const intervalRef = useRef<NodeJS.Timeout>();

  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const onPlayFullscreen = () => {
    if (!timeoutRef.current) {
      // Waiting for video to be ready to play
      timeoutRef.current = setTimeout(async () => {
        try {
          const player = playerRef.current?.player?.player;
          if (!player) return;

          setIsPlaying(true);
          player.unmute();

          await new Promise((resolve, reject) => {
            // Check until the video starts playing, enter full screen mode.
            intervalRef.current = setInterval(() => {
              if (videoRef.current?.webkitEnterFullscreen) {
                videoRef.current.webkitEnterFullscreen();
                resolve(true);
              } else if (videoRef.current?.requestFullscreen) {
                videoRef.current.requestFullscreen();
                resolve(true);
              }
            }, 300);
          });
        } catch (error) {
        } finally {
          clearTimer();
        }
      }, 500);
    }
  };

  const clearTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
    timeoutRef.current = undefined;
    intervalRef.current = undefined;
  }, []);

  const handleFullscreenEvent = useCallback(() => {
    const player = playerRef.current.player?.player;
    if (player) {
      if (screenfull.isFullscreen) {
        player.unmute();
      } else player.mute();
    }
  }, []);

  const onEnter = useCallback(() => {
    if (isAutoPlayOnView) {
      setIsPlaying(true);
    }
  }, [isAutoPlayOnView, setIsPlaying]);

  const onLeave = useCallback(() => {
    setIsPlaying(false);
    playerRef.current.player?.player.mute();
    clearTimer();
  }, [setIsPlaying, clearTimer]);

  const handleOnReady = useCallback(() => {
    if (videoRef.current) return;
    onLoad && onLoad();
    videoRef.current = playerRef.current.player?.player?.player;
    videoRef.current?.addEventListener(
      'fullscreenchange',
      handleFullscreenEvent
    );
  }, [handleFullscreenEvent, onLoad]);

  useEffect(() => {
    return () => {
      videoRef.current?.removeEventListener(
        'fullscreenchange',
        handleFullscreenEvent
      );
    };
  }, [handleFullscreenEvent]);

  return (
    <div className="relative h-full">
      {isFullscreenOnclick && (
        <div
          className="absolute w-full h-full bg-transparent z-[1]"
          onClick={onPlayFullscreen}
        ></div>
      )}
      <Waypoint onEnter={onEnter} onLeave={onLeave}>
        <div className="h-full">
          <ReactPlayer
            ref={playerRef}
            playing={isPlaying}
            loop={loop}
            controls={controls}
            muted
            autoPlay={autoPlay && !isAutoPlayOnView}
            playsinline
            light={
              poster ? (
                <div
                  className={`w-full h-full z-[2] absolute flex justify-center items-center cursor-pointer bg-no-repeat bg-contain bg-center`}
                  onClick={onPlayFullscreen}
                  style={{ backgroundImage: `url('${poster}')` }}
                >
                  <div className="w-full h-full bg-black/10 absolute"></div>
                  <div className="w-1/3 aspect-square rounded-full border-[4px] border-solid border-white relative z-[2]"></div>
                  <div className="w-0 h-0 ml-1 absolute border-t-[14px] border-t-transparent border-l-[26px] border-l-white border-b-[14px] border-b-transparent"></div>
                </div>
              ) : undefined
            }
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  className,
                },
              },
            }}
            url={[
              {
                src: url,
                // TODO: to make quicktime videos work well on iPhone
                type:
                  detectSystemMobile() === ESystemMobile.IOS ? type : undefined,
              },
            ]}
            width={'100%'}
            height={'100%'}
            onReady={handleOnReady}
            onPause={() => {
              setIsPlaying(false);
            }}
            onPlay={() => {
              setIsPlaying(true);
            }}
          />
        </div>
      </Waypoint>
    </div>
  );
}

export default VideoPlayer;
